import { useState, useCallback } from "react";

export type RepeatPattern = "none" | "daily" | "weekly" | "biweekly" | "monthly";

export interface JobFormData {
  jobId: string;
  beginSelectedDate: string;
  endSelectedDate: string;
  company: string;
  location: string;
  numberOfWorkers: number;
  repeatPattern: RepeatPattern;
  repeatOccurrences: number;
}

export interface ValidationColors {
  jobIdColor: string;
  companyColor: string;
  locationColor: string;
  numWerkersColor: string;
  beginDateColor: string;
  endDateColor: string;
  repeatColor: string;
  repeatOccColor: string;
}

/**
 * Custom hook for managing job form validation and field color states
 * Consolidates validation logic from ScheduleJob component
 */
export const useJobFormValidation = () => {
  // Color states for validation feedback
  const [jobIdColor, setJobIdColor] = useState("dark");
  const [companyColor, setCompanyColor] = useState("dark");
  const [locationColor, setLocationColor] = useState("dark");
  const [numWerkersColor, setNumWerkersColor] = useState("dark");
  const [beginDateColor, setBeginDateColor] = useState("dark");
  const [endDateColor, setEndDateColor] = useState("dark");
  const [repeatColor, setRepeatColor] = useState("dark");
  const [repeatOccColor, setRepeatOccColor] = useState("dark");

  /**
   * Reset all validation colors to default "dark"
   */
  const resetColors = useCallback(() => {
    setJobIdColor("dark");
    setCompanyColor("dark");
    setLocationColor("dark");
    setNumWerkersColor("dark");
    setBeginDateColor("dark");
    setEndDateColor("dark");
    setRepeatColor("dark");
    setRepeatOccColor("dark");
  }, []);

  /**
   * Validate job form fields and update color states accordingly
   * @param formData - The job form data to validate
   * @returns boolean - true if form is valid, false otherwise
   */
  const validate = useCallback(
    (formData: JobFormData): boolean => {
      let formComplete = true;

      // Reset colors before validation
      resetColors();

      // Required: Job ID
      if (!formData.jobId) {
        setJobIdColor("danger");
        formComplete = false;
      }

      // Required: Start date
      if (!formData.beginSelectedDate) {
        setBeginDateColor("danger");
        formComplete = false;
      }

      // Required: End date
      if (!formData.endSelectedDate) {
        setEndDateColor("danger");
        formComplete = false;
      }

      // Required: Company
      if (!formData.company) {
        setCompanyColor("danger");
        formComplete = false;
      }

      // Required: Location
      if (!formData.location) {
        setLocationColor("danger");
        formComplete = false;
      }

      // Required: Number of workers (must be >= 1)
      if (!formData.numberOfWorkers || formData.numberOfWorkers < 1) {
        setNumWerkersColor("danger");
        formComplete = false;
      }

      // Date range validation: start date must be before end date
      if (formData.beginSelectedDate > formData.endSelectedDate) {
        setBeginDateColor("danger");
        setEndDateColor("danger");
        formComplete = false;
      }

      // Recurrence validation (only when enabled)
      if (formData.repeatPattern !== "none") {
        if (!formData.repeatOccurrences || formData.repeatOccurrences < 2) {
          setRepeatOccColor("danger");
          formComplete = false;
        }
      }

      // console.log("formComplete =", formComplete);
      return formComplete;
    },
    [resetColors]
  );

  return {
    // Color states
    jobIdColor,
    companyColor,
    locationColor,
    numWerkersColor,
    beginDateColor,
    endDateColor,
    repeatColor,
    repeatOccColor,
    // Color setters (for manual field-specific resets)
    setJobIdColor,
    setCompanyColor,
    setLocationColor,
    setNumWerkersColor,
    setBeginDateColor,
    setEndDateColor,
    setRepeatColor,
    setRepeatOccColor,
    // Functions
    validate,
    resetColors,
  };
};
